import { HoldingServices, IHolding, IOmniaSession, IOmniaShell } from "@omnia/types"
import { IDashboardService } from "../types"
import { Card } from "@omnia/shared/react/Card"
import { isHoldingReadOnly } from "@omnia/shared/session/utils";
import { ReactOpenCropYearDialog } from "./cropYear/OpenCropYearDialog";
import { ReactHoldingEditDialog } from "../../../shared/holding/edit/HoldingEditDialog";
import { IHoldingService } from "@omnia/shared/holding/types";

interface Props {
    svc: IDashboardService
    shell: IOmniaShell
    session: IOmniaSession
    update(run?: () => void): void
}

export function HoldingCard({svc, shell, session, update}: Props) {

    const serviceNames: { [key in keyof Required<Omit<HoldingServices, "HealthySoils">>]: string } = {
        AccessPlus: "Access plus",
        Omnia: "Field manager",
        OmniaPlus: "Field manager plus",
        Precision: "Business manager",
        Connect: "Connect",
        Manure: "Manure",
        SoilManagementPlan: "Soil management plan"
    }
    
    const holding = session.holding;
    const business = session.userBusiness || holding.Business;
    const lastHoldingYear = Math.max.apply(Math, holding.CropYears.map(c => c.YearId));
    const nextAvailableYear = checkNextYear(session, holding);
    const services = getServicesAsText(holding.Services);
    const readOnly = isHoldingReadOnly(session);
    const enablePostTown = shell.config.features.holding.postTown;
        
    function checkNextYear(session: IOmniaSession, holding: IHolding) {

        if (isHoldingReadOnly(session, holding)) {
            return 0
        }
    
        const { yearId: currentYear } = session
    
        if (holding.CropYears.every(y => y.YearId <= currentYear)) {
    
            if (session.getCroppingYears().some(y => y.Year == (currentYear + 1))) {
                return currentYear + 1
            }
        }
    }

    function getServicesAsText(services: HoldingServices): string[] | undefined {
        if (!services) {
            return undefined
        }
    
        return Object.entries(serviceNames).filter(a => Boolean(services[a[0]])).map(a => a[1])
    }

    const manage = () => {
        // refresh the holding data (could be changed outside of the card)
        svc.refresh().then(() => {
            const showDialogPromise = shell.showComponentAsDialog(
                ReactHoldingEditDialog, 
                {
                    holding: svc.holding,
                }, 
                { 
                    backdrop: "static" 
                });
    
            return showDialogPromise.then(() => svc.refresh())
        });
    }

    const canOpenNewYear = (!readOnly) && (!!nextAvailableYear);

    const openNewYear = () => {

        const p = shell.showComponentAsDialog(
            ReactOpenCropYearDialog,
            null,
            {
                size: "lg"
            }
        )

        return p.then(() => svc.refresh());

    }

    const cardTools = !readOnly
        ? <div className="btn-group">
                <button className="btn btn-link btn-icon-toggle" onClick={() => update(() => manage())} title="Edit Holding">
                    <i className="fas fa-pen"></i>
                </button>
            </div>
        : null;
    
    const cardStickyFooter = canOpenNewYear 
        ? <div className="crop-year" onClick={() => update(() => openNewYear())}>
                <div>
                    <i className="icon icon-calendar"></i> The latest year open for your holding is <strong>{lastHoldingYear}</strong>, 
                    and year <strong>{nextAvailableYear}</strong> is available. Click here to open and set up year <strong>{nextAvailableYear}</strong>.
                </div>
            </div>
        : null;

    return <>
        <Card icon='fas fa-home' title="Holding information" small={false} headerTools={cardTools} stickyFooter={cardStickyFooter} isCollapsible={true}>
            <div className="row">
                <div className="col-md-6 col-sm-6">

                    <h4>Business</h4>
                    <p>
                        {business?.Name ?? 'None'}
                    </p>

                    {services !== undefined &&
                        <div>
                            <h4>
                                Services
                                {holding?.PermissionsBasedOnServices &&
                                    <span>
                                        &nbsp;<i className="fa-solid fa-lock text-muted fa-2xs"></i>
                                    </span>
                                }
                            </h4>
                            <ul>
                                {services.map((service, index) => (
                                    <li key={index}>
                                        &ndash;&nbsp;{service}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    }

                </div>
                <div className="col-md-6 col-sm-6">
                    <h4>{holding?.TradingName}</h4>
                    <div>
                        <h5>Address</h5>
                        <div className="render-new-line">{holding?.Address}</div>
                        {enablePostTown && 
                            <>
                                <h5>Post town</h5>
                                {holding?.Town?.Name ?? ''}
                            </>
                        }
                    </div>
                </div>
            </div>
        
        </Card>
    </>
}